/**
 * Seyrek GI v2 — her kare çalışan geçişler ve dokuları.
 *
 * Kare akışı (updateBefore):
 *   1. tracePass     → hücre dokusu   (ekran / stride; hücrede 1 piksel, her kare kayar)
 *   2. fillPass      → doldurma        (tam çözünürlük; 3×3 hücreden aynı yüzeydekiler)
 *   3. refinePass    → kenar taraması  (yalnız doldurmada noktası tutmayan pikseller)
 *   4. historyPass   → geçmiş renk + bilgi (iki doku, her kare yer değiştirir)
 *   5. denoisePass   → çıktı           (0–3 tur; geçmişi kısa olan pikselde güçlü)
 *
 * Çıktı dokusu: vec4( gi.rgb, ao ). Albedo çarpımı ve AO üssü SparseGiV2TslEffect'te.
 *
 * Bellek (1536×1150, RGBA half float): tam çözünürlük 7 doku ≈ 99 MB, hücre dokusu < 1 MB.
 */

import { HalfFloatType, LinearFilter, MathUtils, Matrix4, NearestFilter, NodeMaterial, NodeUpdateType, QuadMesh, RenderTarget, RendererUtils, TempNode, Vector2 } from 'three/webgpu';
import { convertToTexture, passTexture, reference, texture, uniform, vec4 } from 'three/tsl';
import { buildTracePass } from './tracePass.js';
import { buildFillPass } from './fillPass.js';
import { buildRefinePass } from './refinePass.js';
import { buildHistoryColorPass, buildHistoryInfoPass } from './historyPass.js';
import { buildDenoisePass, denoiseStepPixels } from './denoisePass.js';

const _quad = /*@__PURE__*/ new QuadMesh();
const _size = /*@__PURE__*/ new Vector2();
let _rendererState;

// Activision GTAO: kareye göre dilim açısı ve ilk adım kayması.
const TEMPORAL_ROTATIONS = [ 60, 300, 180, 240, 120, 0 ];
const TEMPORAL_OFFSETS = [ 0, 0.5, 0.25, 0.75 ];

export const SPARSE_GI_V2_STRIDES = [ 2, 4, 6, 8, 16, 32 ];
export const SPARSE_GI_V2_MAX_DENOISE_PASSES = 3;

/**
 * Hücre içindeki piksellerin tarama sırası (Bayer). Ardışık kareler birbirinden uzak
 * pikselleri tarar; stride² karede hepsi bir kez taranır. 2'nin katı olmayan boyda (6)
 * bir üst Bayer matrisinin ilk 6×6'sı kullanılır.
 * @return {Array<[number, number]>}
 */
function bayerOrder( size ) {

	let matrix = [ [ 0 ] ];

	while ( matrix.length < size ) {

		const n = matrix.length;
		const next = [];

		for ( let y = 0; y < n * 2; y ++ ) {

			next.push( [] );

			for ( let x = 0; x < n * 2; x ++ ) {

				const quadrant = [ [ 0, 2 ], [ 3, 1 ] ][ Math.floor( y / n ) ][ Math.floor( x / n ) ];
				next[ y ].push( 4 * matrix[ y % n ][ x % n ] + quadrant );

			}

		}

		matrix = next;

	}

	const cells = [];
	for ( let y = 0; y < size; y ++ ) for ( let x = 0; x < size; x ++ ) cells.push( { x, y, rank: matrix[ y ][ x ] } );

	return cells.sort( ( a, b ) => a.rank - b.rank ).map( ( c ) => [ c.x, c.y ] );

}

function createTarget( name, filter ) {

	const target = new RenderTarget( 1, 1, { depthBuffer: false, type: HalfFloatType } );
	target.texture.name = name;
	target.texture.minFilter = filter;
	target.texture.magFilter = filter;
	return target;

}

class SparseGiV2Node extends TempNode {

	static get type() {

		return 'SparseGiV2Node';

	}

	constructor( beautyNode, depthNode, normalNode, camera ) {

		super( 'vec4' );

		this.beautyNode = beautyNode;
		this.depthNode = depthNode;
		this.normalNode = normalNode;
		this.updateBeforeType = NodeUpdateType.FRAME;

		// --- ayarlar (SparseGiV2TslEffect yazar) ---

		this.sliceCount = uniform( 2, 'uint' );
		this.stepCount = uniform( 8, 'uint' );
		this.radius = uniform( 12, 'float' );
		this.thickness = uniform( 1, 'float' );
		this.expFactor = uniform( 2, 'float' );
		this.giIntensity = uniform( 10, 'float' );
		this.backfaceLighting = uniform( 0, 'float' );
		this.useScreenSpaceSampling = uniform( true, 'bool' );
		this.useLinearThickness = uniform( false, 'bool' );
		/** Bir pikselde en fazla kaç kare birikir. 1 = birikim yok. */
		this.historyLength = uniform( 32, 'float' );

		/** Hücre kenarı (piksel): 2, 4, 6, 8, 16 ya da 32. */
		this.stride = 4;
		/** Yumuşatma turu: 0–3. */
		this.denoisePasses = 2;
		/** Doldurmada noktası tutmayan pikselleri ayrıca tara. */
		this.edgeRefine = true;

		// --- iç uniform'lar ---

		this._camera = camera;
		this._reversedDepth = false;
		this._frameIndex = 0;
		this._needsReset = true;

		this._fullResolution = uniform( new Vector2() );
		this._cellResolution = uniform( new Vector2() );
		this._stride = uniform( 4, 'float' );
		this._sampleOffset = uniform( new Vector2() );
		this._refineEnabled = uniform( 1, 'float' );
		this._halfProjScale = uniform( 1, 'float' );
		this._temporalDirection = uniform( 0, 'float' );
		this._temporalOffset = uniform( 0, 'float' );
		this._denoiseSteps = [ 0, 1, 2 ].map( ( i ) => uniform( denoiseStepPixels( 4, i ), 'float' ) );

		this._cameraFar = reference( 'far', 'float', camera );
		this._cameraProjectionMatrixInverse = uniform( camera.projectionMatrixInverse );
		this._cameraWorldMatrix = uniform( camera.matrixWorld );
		this._previousViewMatrix = uniform( new Matrix4() );
		this._previousProjectionMatrix = uniform( new Matrix4() );

		// --- dokular ---

		this._cellTarget = createTarget( 'SparseGiV2.cells', NearestFilter );
		this._fillTarget = createTarget( 'SparseGiV2.fill', NearestFilter );
		this._refineTarget = createTarget( 'SparseGiV2.refine', NearestFilter );
		this._historyColorTargets = [ createTarget( 'SparseGiV2.historyColorA', LinearFilter ), createTarget( 'SparseGiV2.historyColorB', LinearFilter ) ];
		this._historyInfoTargets = [ createTarget( 'SparseGiV2.historyInfoA', NearestFilter ), createTarget( 'SparseGiV2.historyInfoB', NearestFilter ) ];
		this._outputTarget = createTarget( 'SparseGiV2.output', LinearFilter );

		this._cellTexture = texture( this._cellTarget.texture );
		this._fillTexture = texture( this._fillTarget.texture );
		this._refineTexture = texture( this._refineTarget.texture );
		// Bu dördünün dokusu her kare değişir (updateBefore): okunan = geçen kare yazılan.
		this._historyColorRead = texture( this._historyColorTargets[ 1 ].texture );
		this._historyInfoRead = texture( this._historyInfoTargets[ 1 ].texture );
		this._historyColorCurrent = texture( this._historyColorTargets[ 0 ].texture );
		this._historyInfoCurrent = texture( this._historyInfoTargets[ 0 ].texture );

		this._outputNode = passTexture( this, this._outputTarget.texture );
		// passTexture: görünüm bu düğüme bağlı olsun ki hata ayıklama modunda da geçişler çalışsın.
		this._debugInfoNode = passTexture( this, this._historyInfoTargets[ 0 ].texture );

		// --- malzemeler (setup'ta doldurulur) ---

		this._traceMaterial = new NodeMaterial();
		this._fillMaterial = new NodeMaterial();
		this._refineMaterial = new NodeMaterial();
		this._historyColorMaterial = new NodeMaterial();
		this._historyInfoMaterial = new NodeMaterial();
		this._denoiseMaterials = [ new NodeMaterial(), new NodeMaterial(), new NodeMaterial() ];

	}

	getTextureNode() {

		return this._outputNode;

	}

	/** Hata ayıklama: kırmızı = doldurmada tutan nokta yok (kenar taraması), yeşil = geçmiş uzunluğu. */
	getDebugNode() {

		const info = this._debugInfoNode;
		return vec4( info.z, info.x.div( this.historyLength ), 0, 1 );

	}

	/**
	 * Hata ayıklama: bu kare pikselin GI'sı nereden geldi?
	 * Mavi = bu kare taranan nokta (kendisi aldı), yeşil = komşu hücrelerin noktalarından,
	 * kırmızı = hiçbir komşu tutmadı (kenar taraması açıksa kendisi taradı, kapalıysa geçmişten kaldı),
	 * siyah = gökyüzü.
	 */
	getSourceDebugNode() {

		const info = this._debugInfoNode;

		const isSky = info.y.equal( 0 );
		const fromEdgeTrace = info.z.greaterThan( 0.5 );
		const isSamplePoint = info.w.greaterThan( 0.5 );

		const fromNeighbours = isSamplePoint.select( vec4( 0, 0, 1, 1 ), vec4( 0, 1, 0, 1 ) );
		const source = fromEdgeTrace.select( vec4( 1, 0, 0, 1 ), fromNeighbours );

		return isSky.select( vec4( 0, 0, 0, 1 ), source );

	}

	/** Ayar değişti, eski birikim yanlış: bir sonraki kare her piksel sıfırdan başlar. */
	resetHistory() {

		this._needsReset = true;

	}

	setSize( width, height ) {

		const stride = SPARSE_GI_V2_STRIDES.includes( this.stride ) ? this.stride : 4;
		const cellWidth = Math.ceil( width / stride );
		const cellHeight = Math.ceil( height / stride );

		const sizeChanged = this._fullResolution.value.x !== width || this._fullResolution.value.y !== height;
		const strideChanged = this._stride.value !== stride;

		if ( sizeChanged ) {

			this._fullResolution.value.set( width, height );
			for ( const target of this._fullResolutionTargets() ) target.setSize( width, height );
			this._needsReset = true;

		}

		if ( sizeChanged || strideChanged ) {

			this._stride.value = stride;
			this._cellResolution.value.set( cellWidth, cellHeight );
			this._cellTarget.setSize( cellWidth, cellHeight );
			this._sampleOrder = bayerOrder( stride );
			this._denoiseSteps.forEach( ( step, i ) => ( step.value = denoiseStepPixels( stride, i ) ) );
			this._needsReset = true;

		}

		this._halfProjScale.value = height / ( Math.tan( this._camera.fov * MathUtils.DEG2RAD * 0.5 ) * 2 ) * 0.5;

	}

	updateBefore( frame ) {

		const { renderer } = frame;

		_rendererState = RendererUtils.resetRendererState( renderer, _rendererState );

		const size = renderer.getDrawingBufferSize( _size );
		this.setSize( size.width, size.height );

		// Bu kare hücrenin hangi pikseli taranacak, gürültü hangi yöne dönecek.
		const [ offsetX, offsetY ] = this._sampleOrder[ this._frameIndex % this._sampleOrder.length ];
		this._sampleOffset.value.set( offsetX, offsetY );
		this._temporalDirection.value = TEMPORAL_ROTATIONS[ this._frameIndex % TEMPORAL_ROTATIONS.length ] / 360;
		this._temporalOffset.value = TEMPORAL_OFFSETS[ this._frameIndex % TEMPORAL_OFFSETS.length ];
		this._refineEnabled.value = this.edgeRefine ? 1 : 0;

		// Geçmiş dokuları: geçen kare yazılan okunur, öbürüne yazılır.
		const write = this._frameIndex % 2;
		const read = 1 - write;
		this._historyColorRead.value = this._historyColorTargets[ read ].texture;
		this._historyInfoRead.value = this._historyInfoTargets[ read ].texture;
		this._historyColorCurrent.value = this._historyColorTargets[ write ].texture;
		this._historyInfoCurrent.value = this._historyInfoTargets[ write ].texture;
		this._debugInfoNode.value = this._historyInfoTargets[ write ].texture;

		renderer.setClearColor( 0x000000, 0 );

		if ( this._needsReset ) {

			// Bilgi dokusu 0 = geçmiş uzunluğu 0: her piksel yeniden başlar.
			for ( const target of this._historyInfoTargets ) {

				renderer.setRenderTarget( target );
				renderer.clear();

			}

			this._needsReset = false;

		}

		this._renderPass( renderer, this._traceMaterial, this._cellTarget );
		this._renderPass( renderer, this._fillMaterial, this._fillTarget );
		if ( this.edgeRefine ) this._renderPass( renderer, this._refineMaterial, this._refineTarget );
		this._renderPass( renderer, this._historyColorMaterial, this._historyColorTargets[ write ] );
		this._renderPass( renderer, this._historyInfoMaterial, this._historyInfoTargets[ write ] );
		this._renderDenoise( renderer, this._historyColorTargets[ write ] );

		// Bir sonraki kare için bu karenin kamerası "önceki kamera" olur.
		this._previousViewMatrix.value.copy( this._camera.matrixWorldInverse );
		this._previousProjectionMatrix.value.copy( this._camera.projectionMatrix );
		this._frameIndex ++;

		RendererUtils.restoreRendererState( renderer, _rendererState );

	}

	_renderPass( renderer, material, target ) {

		renderer.setRenderTarget( target );
		_quad.material = material;
		_quad.name = material.name;
		_quad.render( renderer );

	}

	/** Turlar sırayla doldurma ve kenar dokularını ara doku olarak kullanır; son tur çıktıya yazar. */
	_renderDenoise( renderer, historyTarget ) {

		const passes = Math.min( SPARSE_GI_V2_MAX_DENOISE_PASSES, Math.max( 0, Math.round( this.denoisePasses ) ) );

		if ( passes === 0 ) {

			renderer.copyTextureToTexture( historyTarget.texture, this._outputTarget.texture );
			return;

		}

		const scratchTargets = [ this._fillTarget, this._refineTarget ];

		for ( let i = 0; i < passes; i ++ ) {

			const target = i === passes - 1 ? this._outputTarget : scratchTargets[ i ];
			this._renderPass( renderer, this._denoiseMaterials[ i ], target );

		}

	}

	_fullResolutionTargets() {

		return [ this._fillTarget, this._refineTarget, ...this._historyColorTargets, ...this._historyInfoTargets, this._outputTarget ];

	}

	setup( builder ) {

		this._reversedDepth = builder.renderer.reversedDepthBuffer === true;

		const context = builder.getSharedContext();
		const assign = ( material, name, fragmentNode ) => {

			material.name = name;
			material.fragmentNode = fragmentNode.context( context );
			material.needsUpdate = true;

		};

		// Yumuşatma turu i'nin okuduğu doku: ilk tur geçmiş, sonrakiler bir önceki turun yazdığı.
		const denoiseInputs = [ this._historyColorCurrent, this._fillTexture, this._refineTexture ];

		assign( this._traceMaterial, 'SparseGiV2.trace', buildTracePass( this ) );
		assign( this._fillMaterial, 'SparseGiV2.fill', buildFillPass( this ) );
		assign( this._refineMaterial, 'SparseGiV2.refine', buildRefinePass( this ) );
		assign( this._historyColorMaterial, 'SparseGiV2.historyColor', buildHistoryColorPass( this ) );
		assign( this._historyInfoMaterial, 'SparseGiV2.historyInfo', buildHistoryInfoPass( this ) );
		this._denoiseMaterials.forEach( ( material, i ) => {

			assign( material, `SparseGiV2.denoise${i}`, buildDenoisePass( this, denoiseInputs[ i ], this._denoiseSteps[ i ] ) );

		} );

		return this._outputNode;

	}

	dispose() {

		this._cellTarget.dispose();
		for ( const target of this._fullResolutionTargets() ) target.dispose();

		this._traceMaterial.dispose();
		this._fillMaterial.dispose();
		this._refineMaterial.dispose();
		this._historyColorMaterial.dispose();
		this._historyInfoMaterial.dispose();
		for ( const material of this._denoiseMaterials ) material.dispose();

	}

}

export default SparseGiV2Node;

/**
 * @param {Node} beautyNode - direkt ışıklı sahne (GI'nın kaynağı)
 * @param {TextureNode} depthNode
 * @param {TextureNode} normalNode - görüş uzayı normali (MRT)
 * @param {PerspectiveCamera} camera
 */
export const sparseGiV2 = ( beautyNode, depthNode, normalNode, camera ) =>
	new SparseGiV2Node( convertToTexture( beautyNode ), depthNode, normalNode, camera );
